import { BrowseEventsInLocationSelector } from '@/components/BrowseEventsInLocationSelector';
import { EventsBucket } from '@/components/EventsBucket';
import { filterUnactionableEvents } from '@/shared/utils/filterUnactionableEvents';
import { IconCategoryBrowse } from '@/components/IconCategoryBrowse';
import { RootState } from '@/redux/store';
import { locationChange } from '@/redux/location';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BrowseTabs } from '@/components/BrowseTabs/BrowseTabs';
import { ALL_TAB, FOR_YOU, TABS_CONFIG } from '@/constants/tabConfig';
import styles from './BrowseContainer.module.scss';
import { getLastRecentSearchTerms, getLastRecentViewsList } from '@/components/utils/utils';
import { mergeFeedWithEarlyAccess } from '@/shared/utils/mergeFeedWithEarlyAccess';
import { selectLocale } from '@/redux/app';
import { useAppSelector } from '@/redux/hooks';
import { sdkRequest } from '@eventbrite/legacy-infra.http';
import { getPromotedEventsOptions } from './promotedUtils';
import { EventInteraction, aggregateContext } from '@/utils/tracking';
import { useTracking } from '@/tracking';
import { ForYou } from '@/components/ForYou/ForYou';
import { useClientAuth } from '@/common/useUserPublicId';
import { useUserSubscription } from '@/common/useUserSubscription';
import { useIsEventbritePlusEnabled } from '@/bsp/config';
import {
    EARLY_ACCESS_EVENT_RANKS,
    PROMOTED_EVENTS_RANKS,
    PROMOTED_EVENTS_RANKS_WITH_EARLY_ACCESS,
} from '@/constants/promoted';
import type { bboxShape } from '@eventbrite/consumer-search.discover-utils/types';
import { DestinationEvent, FormattedEvent } from '@eventbrite/consumer-search.event-renderer';

interface BrowseContainerProps extends PropsFromRedux {
    userId: string;
}

export function convertBboxToSearchFormat(
    bboxData: bboxShape | null | undefined,
): { top: number; left: number; bottom: number; right: number } | undefined {
    if (!bboxData?.sw?.lat || !bboxData?.ne?.lat) return undefined;

    return {
        top: bboxData.ne.lat,
        left: bboxData.sw.lng,
        bottom: bboxData.sw.lat,
        right: bboxData.ne.lng,
    };
}

export const BrowseContainer = (props: BrowseContainerProps) => {
    const { location, setUserLocation, userId } = props;
    const locale = useAppSelector(selectLocale);
    const [searchDataEvents, setSearchDataEvents] = useState<{
        organicEvents: DestinationEvent[];
        promotedEvents: DestinationEvent[];
    }>({ organicEvents: [], promotedEvents: [] });
    // Members get a second, availability-unfiltered search so not-yet-on-sale early access events can be placed.
    const [earlyAccessEvents, setEarlyAccessEvents] = useState<DestinationEvent[]>([]);
    // we don't want to fetch the events until we have finished getting the location
    // By default after SSr the location will be null, we don't want to fire the call to get default evetns just yet
    // By using isInitialLoad, we wait for that call to be over or failed, and after it is over, we use the response to fetch the events corresponding to the location
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentTab, setCurrentTab] = useState<string>(ALL_TAB);
    // The For-you tab owns its own feed (recommendations + its own promoted call), so it hands its rendered list back
    // for ranking instead of going through searchDataEvents.
    const [forYouEvents, setForYouEvents] = useState<FormattedEvent[]>([]);
    const [isPending, startTransition] = useTransition();
    // Lets a fetch that is superseded by a newer tab/location change drop its result instead of painting it.
    const latestFetchRequestId = useRef(0);

    const { authState, publicUserId } = useClientAuth();
    const { isEnabled: isEventbritePlusEnabled } = useIsEventbritePlusEnabled();
    const { hasEarlyAccessPurchaseWindows, isSubscriptionReady } =
        useUserSubscription(publicUserId);
    const showEarlyAccessForViewer = isEventbritePlusEnabled && hasEarlyAccessPurchaseWindows;
    // A member's first paint must already know about early access, so the feed waits for auth and the subscription lookup.
    const isFeedEligibilitySettled = authState !== 'unknown' && isSubscriptionReady;
    const isEarlyAccessEventForViewer = useCallback(
        (event: DestinationEvent) =>
            showEarlyAccessForViewer &&
            event.ticket_availability?.has_early_access_tickets === true &&
            event.event_sales_status?.sales_status === 'not_yet_on_sale',
        [showEarlyAccessForViewer],
    );

    // Early access and ads are placed at fixed slots, so the ranked list (and therefore the tracking positions)
    // must match the list that actually renders. Filtering also lives here, not at fetch time, because the member
    // gate resolves after the response and a member's not-yet-on-sale early access events have to survive it.
    const orderedEvents = useMemo(() => {
        const { organicEvents, promotedEvents } = searchDataEvents;
        const filterOptions = {
            includeNotYetOnSaleEarlyAccess: showEarlyAccessForViewer,
        };
        const filteredOrganic = filterUnactionableEvents(organicEvents, filterOptions);
        const filteredPromoted = filterUnactionableEvents(promotedEvents, filterOptions);
        const promotedIds = new Set(filteredPromoted.map(({ id }) => id));
        const organicIds = new Set(filteredOrganic.map(({ id }) => id));
        const primaryEarlyAccess = filteredOrganic.filter(isEarlyAccessEventForViewer);
        const candidateEarlyAccess = earlyAccessEvents.filter(
            (event) =>
                isEarlyAccessEventForViewer(event) &&
                !organicIds.has(event.id) &&
                !promotedIds.has(event.id),
        );
        const earlyAccessQueue = [...primaryEarlyAccess, ...candidateEarlyAccess].slice(
            0,
            EARLY_ACCESS_EVENT_RANKS.length,
        );
        const earlyAccessIds = new Set(earlyAccessQueue.map(({ id }) => id));
        // The paid copy wins the merge, so organic duplicates of ads and of early access cards are dropped.
        const organicQueue = filteredOrganic.filter(
            (event) => !promotedIds.has(event.id) && !earlyAccessIds.has(event.id),
        );

        return mergeFeedWithEarlyAccess({
            organicEvents: organicQueue,
            earlyAccessEvents: earlyAccessQueue,
            promotedEvents: filteredPromoted,
            earlyAccessRanks: EARLY_ACCESS_EVENT_RANKS,
            promotedRanks:
                earlyAccessQueue.length > 0
                    ? PROMOTED_EVENTS_RANKS_WITH_EARLY_ACCESS
                    : PROMOTED_EVENTS_RANKS,
        }) as DestinationEvent[];
    }, [
        searchDataEvents,
        earlyAccessEvents,
        isEarlyAccessEventForViewer,
        showEarlyAccessForViewer,
    ]);

    useEffect(() => {
        if (publicUserId) {
            setCurrentTab(FOR_YOU);
        }
    }, [publicUserId]);

    useEffect(() => {
        const fetchUserLocation = async () => {
            try {
                const response = await fetch('/home/api/geo/place_from_request/');
                if (!response.ok) {
                    throw new Error('Failed to fetch user location');
                }
                const { place } = await response.json();
                setUserLocation(place);
            } catch (e) {
                console.error('Error fetching user location:', e);
            } finally {
                setIsInitialLoad(false);
            }
        };
        fetchUserLocation();
    }, []);

    const fetchEventsForTab = async (tabKey: string) => {
        const requestId = ++latestFetchRequestId.current;
        setIsLoading(true);

        if (tabKey === FOR_YOU) {
            setSearchDataEvents({ organicEvents: [], promotedEvents: [] });
            setIsLoading(false);
            return;
        }

        try {
            const placeId = location?.placeId;
            const bbox = convertBboxToSearchFormat(location?.bbox);

            const organicEventsReq = fetch(`/home/api/search/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    placeId,
                    tab: tabKey,
                    bbox,
                }),
            });
            const promotedEventsReq = sdkRequest(
                '/promoted/events/',
                getPromotedEventsOptions(location, TABS_CONFIG[currentTab], {
                    recentSearches: getLastRecentSearchTerms(10),
                    recentViews: getLastRecentViewsList(10),
                }),
                '/home',
            );
            const earlyAccessEventsReq = showEarlyAccessForViewer
                ? fetch('/home/api/search/', {
                      method: 'POST',
                      headers: {
                          'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                          placeId,
                          tab: tabKey,
                          bbox,
                          includeNotYetOnSale: true,
                      }),
                  })
                      .then(async (response) => {
                          if (!response.ok) {
                              throw new Error('Failed to fetch early access events');
                          }
                          const data = await response.json();
                          return Array.isArray(data?.events) ? data.events : [];
                      })
                      .catch((error) => {
                          console.error('Error fetching early access events:', error);
                          return [];
                      })
                : Promise.resolve([]);

            const [organicEventsRes, promotedEventsRes, earlyAccessEvents] = await Promise.all([
                organicEventsReq.catch(() => {
                    throw new Error('Failed to fetch organic events');
                }),
                promotedEventsReq.catch(() => []),
                earlyAccessEventsReq,
            ]);

            const organicData = await organicEventsRes.json();
            const promotedData: DestinationEvent[] = Array.isArray(promotedEventsRes)
                ? promotedEventsRes
                : [];

            // Functional updates re-check the guard when the update commits, so a response superseded while
            // queued in the transition never paints.
            startTransition(() => {
                setSearchDataEvents((previous) =>
                    requestId !== latestFetchRequestId.current
                        ? previous
                        : {
                              organicEvents: organicData?.events ?? [],
                              promotedEvents: promotedData,
                          },
                );
                setEarlyAccessEvents((previous) =>
                    requestId !== latestFetchRequestId.current ? previous : earlyAccessEvents,
                );
                setIsLoading((previous) =>
                    requestId !== latestFetchRequestId.current ? previous : false,
                );
            });
        } catch (error) {
            console.error(`Error fetching search data events for tab: ${tabKey}`, error);
            setSearchDataEvents((previous) =>
                requestId !== latestFetchRequestId.current
                    ? previous
                    : { organicEvents: [], promotedEvents: [] },
            );
            setEarlyAccessEvents((previous) =>
                requestId !== latestFetchRequestId.current ? previous : [],
            );
            setIsLoading((previous) =>
                requestId !== latestFetchRequestId.current ? previous : false,
            );
        }
    };

    useEffect(() => {
        if (!isInitialLoad && isFeedEligibilitySettled) {
            fetchEventsForTab(currentTab);
        }
    }, [
        location.placeId,
        location.bbox,
        currentTab,
        isInitialLoad,
        showEarlyAccessForViewer,
        isFeedEligibilitySettled,
    ]);

    const handleTabChange = (tab: string) => {
        if (tab === currentTab) {
            return;
        }
        // Invalidate in-flight responses at click time, before the tab commits and before the next effect runs.
        latestFetchRequestId.current += 1;
        startTransition(() => {
            setCurrentTab(tab);
        });
    };

    // Stable identity so ForYou's reporting effect doesn't re-fire on every BrowseContainer render.
    const handleForYouEventsChange = useCallback((events: FormattedEvent[]) => {
        setForYouEvents(events);
    }, []);

    // Rank against the same list the active tab actually renders, ad slots and early access order included.
    const renderedEvents = useMemo(
        () => (currentTab === FOR_YOU ? forYouEvents : orderedEvents),
        [currentTab, forYouEvents, orderedEvents],
    );

    const getTrackingContext = ({ id, event }: EventInteraction) =>
        aggregateContext({
            id,
            event,
            events: renderedEvents,
            userId,
            locale,
            tabKey: currentTab,
            location,
        });
    // Local tracker fans each interaction out to both log_engagement and PicoX
    // (product_location: 'homepage') from one deduped, batched stream.
    const { ref } = useTracking<HTMLDivElement>(getTrackingContext);

    return (
        <div className={styles.root} data-testid="browse-section" ref={ref}>
            <IconCategoryBrowse />
            <hr className={styles.border} />
            <div className={classNames(styles.horizontalGutters, styles.largeMaxWidth)}>
                <BrowseEventsInLocationSelector location={location} />
            </div>
            <hr className={styles.border} />
            <div className={classNames(styles.horizontalGutters, styles.largeMaxWidth)}>
                <BrowseTabs onTabChange={handleTabChange} selectedTab={currentTab} />
                {currentTab !== FOR_YOU ? (
                    <EventsBucket
                        loadingState={{ isFeLoading: isLoading || isPending }}
                        events={orderedEvents}
                        currentTab={currentTab}
                        authState={authState}
                        publicUserId={publicUserId}
                        includeNotYetOnSaleEarlyAccess={showEarlyAccessForViewer}
                    />
                ) : (
                    <ForYou
                        location={location}
                        // isInitialLoad flips false in the geo call's `finally`, so this reports "we tried", not "we
                        // found one" — a failed lookup releases the feed to render ad-free instead of spinning.
                        isLocationSettled={!isInitialLoad}
                        onRenderedEventsChange={handleForYouEventsChange}
                    />
                )}
            </div>
        </div>
    );
};

const _mapStateToProps = (state: RootState) => ({
    // isAuthenticated: Boolean(state.user.isAuthenticated),
    // isMobile: state.env.isMobile,
    // tld: state.env?.localeInfo?.tld,
    // tabKey: state.content.browseState?.tabKey,
    // loadingState: state.app.loadingState,
    location: state.location,
    // seasonalContent: state.content.seasonal,
    // featureFlags: state.app.featureFlags,
    // useNewHeader: state.header.forceSpecialStyles,
});

const _mapDispatchToProps = {
    // fetchEventsForTab,
    // fetchUserSavedEvents,
    setUserLocation: locationChange,
};

export const connector = connect(_mapStateToProps, _mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ConnectedBrowseContainer = connector(BrowseContainer);
